import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MapPin, X, Loader2 } from 'lucide-react';
import { getAddressSuggestions } from '../services/geocodingApi';

const PRIMARY = '#054625';

const AddressPopup = ({ onSelect, onClose }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 3) {
      setSuggestions([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    setError(null);
    const timeoutId = setTimeout(async () => {
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      try {
        const results = await getAddressSuggestions(q, controller.signal);
        setSuggestions(results);
      } catch (e) {
        if (e.name !== 'AbortError') {
          setError(t('addressPopup.loadError'));
          setSuggestions([]);
        }
      } finally {
        setLoading(false);
      }
    }, 350);
    return () => clearTimeout(timeoutId);
  }, [query]);

  useEffect(() => {
    return () => { if (abortRef.current) abortRef.current.abort(); };
  }, []);

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 10001, background: 'rgba(5,16,4,0.5)', display: 'flex', alignItems: 'flex-end' }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#FCFBF5', width: '100%', maxHeight: '80vh', borderRadius: '28px 28px 0 0',
          padding: '22px 20px calc(20px + env(safe-area-inset-bottom))', display: 'flex', flexDirection: 'column', gap: '16px',
          boxShadow: '0 -14px 40px rgba(5,16,4,0.2)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <strong style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 600, fontSize: '19px' }}>{t('addressPopup.title')}</strong>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('cartSidebar.close')}
            style={{ marginLeft: 'auto', border: 'none', background: '#FBF9D9', borderRadius: '999px', width: '32px', height: '32px', display: 'grid', placeItems: 'center', cursor: 'pointer', color: '#051004' }}
          >
            <X size={16} />
          </button>
        </div>

        <div style={{ position: 'relative' }}>
          <MapPin size={18} style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: PRIMARY }} />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('addressPopup.inputPlaceholder')}
            style={{
              width: '100%', border: '1px solid rgba(5,16,4,0.14)', borderRadius: '999px', padding: '14px 16px 14px 44px',
              fontFamily: 'Poppins, sans-serif', fontSize: '16px', background: '#FFFFFF', color: '#051004', outline: 'none',
            }}
          />
        </div>

        <div style={{ overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '2px', minHeight: '80px' }}>
          {loading && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '14px 6px', color: 'rgba(5,16,4,0.55)', fontFamily: 'Archivo, sans-serif', fontSize: '14px' }}>
              <Loader2 size={16} className="animate-spin" /> {t('addressPopup.searching')}
            </div>
          )}
          {!loading && error && (
            <div style={{ padding: '14px 6px', color: '#EF4C23', fontFamily: 'Archivo, sans-serif', fontSize: '14px' }}>{error}</div>
          )}
          {!loading && !error && query.trim().length >= 3 && suggestions.length === 0 && (
            <div style={{ padding: '14px 6px', color: 'rgba(5,16,4,0.5)', fontFamily: 'Archivo, sans-serif', fontSize: '14px' }}>
              {t('addressPopup.noAddressFound')}
            </div>
          )}
          {!loading && suggestions.map((s, i) => (
            <button
              key={`${s.lat}-${s.lon}-${i}`}
              type="button"
              onClick={() => onSelect(s)}
              style={{
                display: 'flex', alignItems: 'flex-start', gap: '10px', textAlign: 'left', border: 'none',
                background: 'transparent', cursor: 'pointer', padding: '12px 6px', borderRadius: '14px',
                fontFamily: 'Poppins, sans-serif', fontSize: '14.5px', color: '#051004',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#FBF9D9'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              <MapPin size={16} style={{ flexShrink: 0, marginTop: '2px', color: 'rgba(5,16,4,0.4)' }} />
              <span>{s.display_name}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AddressPopup;
